import { Maybe } from 'maybeasy';
import * as React from 'react';
import { ProgramSegment } from '../../../ProgramStore/Types';
import { NotTranslated, T } from '../../../Translations';
import CompleteSegmentIcon from '../../Cutesies/CompleteSegmentIcon';
import CurrentSegmentIcon from '../../Cutesies/CurrentSegmentIcon';
import UnstartedSegmentIcon from '../../Cutesies/UnstartedSegmentIcon';
import TimeToComplete from '../../TimeToComplete';
import OutlineLockIcon from '../OutlineLockIcon';
import { isSegmentLocked } from '../locked';
import * as style from '../style.module.css';

interface Props {
  segment: ProgramSegment;
  currentSegmentId: Maybe<number>;
  viewedSegmentReady: boolean;
  onNavigate: (segment: ProgramSegment) => void;
  latestAvailableSegmentId: Maybe<number>;
}

const OutlineSegment: React.FC<Props> = ({
  segment,
  currentSegmentId,
  viewedSegmentReady,
  onNavigate,
  latestAvailableSegmentId,
}) => {
  // "You're here" tracks the segment actually loaded in the view; "Resume" marks the
  // furthest reachable (first accessible) segment. The icon reflects the segment's own
  // state; the pills carry the you're-here / resume labels.
  const isViewing = currentSegmentId.map((id) => id === segment.id).getOrElseValue(false);
  const isResumePoint = latestAvailableSegmentId
    .map((id) => id === segment.id)
    .getOrElseValue(false);
  const isLocked = isSegmentLocked(segment);
  // Show the completed check when actually complete, or the moment Next activates on the
  // segment you're viewing (same signal as the Next button).
  const showsCompleted = segment.status === 'Complete' || (isViewing && viewedSegmentReady);

  const segmentClass = () => {
    if (isViewing) return style.current;
    if (segment.status === 'Complete') return style.complete;
    return style.inaccessible;
  };

  // One state -> one icon (priority: completed > locked > resume > unstarted), so the icon
  // selection is exhaustive and can never produce more than one icon.
  type SegmentIconState = 'completed' | 'locked' | 'resume' | 'unstarted';
  const segmentIconState = (): SegmentIconState => {
    if (showsCompleted) return 'completed';
    if (isLocked) return 'locked';
    if (isResumePoint) return 'resume';
    return 'unstarted';
  };

  const SegmentIcon: React.FC = () => {
    switch (segmentIconState()) {
      case 'completed':
        return <CompleteSegmentIcon />;
      case 'locked':
        return <OutlineLockIcon />;
      case 'resume':
        return <CurrentSegmentIcon />; // the play/arrow = resume
      case 'unstarted':
        return <UnstartedSegmentIcon />;
    }
  };

  const SegmentBody: React.FC = () => (
    <>
      <div className={style.segmentIcon} data-test-segment-icon={segment.status}>
        <SegmentIcon />
        <div className={style.segmentLine} />
      </div>
      <div className={style.segmentLabel} data-test-segment-title={segment.title}>
        <NotTranslated text={segment.title} />
        {showsCompleted && (
          <span className={style.srOnly}>
            <T kind="Completed" />
          </span>
        )}
        {isLocked && (
          <span className={style.srOnly}>
            <T kind="Locked" />
          </span>
        )}
        {isViewing && (
          <span className={style.hereTag} data-test-here-tag={segment.id}>
            <T kind="You're here" />
          </span>
        )}
        {isResumePoint && !isViewing && (
          <span className={style.resumeTag} data-test-resume-tag={segment.id}>
            <T kind="Resume" />
          </span>
        )}
        <TimeToComplete ttc={segment.timeToComplete} />
      </div>
    </>
  );

  if (isLocked) {
    return (
      <li className={style.segment} data-test-outline-segment={segment.id}>
        <div
          className={`${style.inaccessible} ${style.locked}`}
          tabIndex={0}
          aria-disabled={true}
          data-test-segment-locked={segment.id}
        >
          <SegmentBody />
          <span className={style.lockTooltip} role="tooltip">
            <T kind="Segments must be completed in order. Please ..." />
          </span>
        </div>
      </li>
    );
  }

  return (
    <li className={style.segment} data-test-outline-segment={segment.id}>
      <button
        type="button"
        onClick={() => onNavigate(segment)}
        className={`${segmentClass()} ${style.segmentLink}`}
        data-intercom-target="outline-segment-link"
        data-test-segment-link={segment.id}
      >
        <SegmentBody />
      </button>
    </li>
  );
};
export default OutlineSegment;
